import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/App.css';
import { updateNidStatus, fetchNidVerifications, verifyNid } from '../services/api';

const EMPTY_FILTERS = {
  nid_no: '',
  mobile_no: '',
  is_verified: '',
  status: '',
  dob: '',
  created_at: '',
  end_at: ''
};

const NidVerify = () => {
  const [nidNumber, setNidNumber] = useState('');
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedNid, setSelectedNid] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentData, setCurrentData] = useState({});
  const [changed, setChanged] = useState(false);

  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    total_pages: 1,
    total_items: 0,
    has_prev: false,
    has_next: false
  });

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(true);

  const loadVerifications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await fetchNidVerifications({
        page: pagination.current_page,
        per_page: pagination.per_page,
        ...appliedFilters,
      });

      if (result.success) {
        setVerifications(result.data || []);
        setPagination((prev) => ({ ...prev, ...(result.pagination || {}) }));
      } else {
        throw new Error(result.message || 'Failed to fetch verifications');
      }
    } catch (err) {
      console.error('Error loading verifications:', err);
      setError(err?.message || 'You have no permission to access this resource');
      setVerifications([]);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pagination.current_page, pagination.per_page]);

  useEffect(() => {
    loadVerifications();
  }, [loadVerifications, changed]);

  const resetStates = () => {
    setShowModal(false);
    setSelectedNid(null);
    setCurrentData({});
  };

  const handleVerify = async () => {
    if (!nidNumber.trim()) {
      setError('Please enter a NID number');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await verifyNid(nidNumber);

      if (result.success) {
        setNidNumber('');
        setPagination((prev) => ({ ...prev, current_page: 1 }));
        setChanged((p) => !p);
      } else {
        throw new Error(result.message || 'Failed to verify NID');
      }
    } catch (err) {
      console.error('Error verifying NID:', err);
      setError(err.message || 'Failed to verify NID');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, current_page: newPage }));
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const getStatusClass = (status, isVerified) => {
    if (isVerified) return 'status-completed';
    if (status === 'success') return 'status-completed';
    if (status === 'failed') return 'status-failed';
    return 'status-busy';
  };

  const getStatusText = (status, isVerified) => {
    if (isVerified) return 'Verified';
    if (status === 'success') return 'Success';
    if (status === 'failed') return 'Failed';
    return 'Pending';
  };

  const startItem = useMemo(
    () => (pagination.current_page - 1) * pagination.per_page + 1,
    [pagination.current_page, pagination.per_page]
  );

  const endItem = useMemo(
    () => Math.min(pagination.current_page * pagination.per_page, pagination.total_items),
    [pagination.current_page, pagination.per_page, pagination.total_items]
  );

  return (
    <div className="content">
      <div className="main-content">
        <h1 className="page-title">NID VERIFICATIONS</h1>

        {error && <div className="error-alert">{error}</div>}

        {showFilters && (
          <div className="filters-panel">
            <div className="filters-grid">
              <div className="filter-item">
                <label className="filter-label">NID Number</label>
                <input
                  type="text"
                  className="filter-input"
                  value={filters.nid_no}
                  onChange={(e) => handleFilterChange('nid_no', e.target.value)}
                  placeholder="Search NID..."
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">Mobile Number</label>
                <input
                  type="text"
                  className="filter-input"
                  value={filters.mobile_no}
                  onChange={(e) => handleFilterChange('mobile_no', e.target.value)}
                  placeholder="Search mobile..."
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">Status</label>
                <select
                  className="filter-select"
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="pending">Pending</option>
                </select>
              </div>

              <div className="filter-item">
                <label className="filter-label">Verified</label>
                <select
                  className="filter-select"
                  value={filters.is_verified}
                  onChange={(e) => handleFilterChange('is_verified', e.target.value)}
                >
                  <option value="">All</option>
                  <option value="true">Verified</option>
                  <option value="false">Not Verified</option>
                </select>
              </div>

              <div className="filter-item">
                <label className="filter-label">Date of Birth</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.dob}
                  onChange={(e) => handleFilterChange('dob', e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">Created From</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.created_at}
                  onChange={(e) => handleFilterChange('created_at', e.target.value)}
                />
              </div>

              <div className="filter-item">
                <label className="filter-label">Created To</label>
                <input
                  type="date"
                  className="filter-input"
                  value={filters.end_at}
                  onChange={(e) => handleFilterChange('end_at', e.target.value)}
                />
              </div>
            </div>

            <div className="filter-actions">
              <button onClick={handleApplyFilters} className="btn-apply-filter">
                Apply Filters
              </button>
              <button onClick={handleClearFilters} className="btn-clear-filter">
                Clear Filters
              </button>
            </div>
          </div>
        )}

        <div className="table-container">
          {loading ? (
            <div className="loading-cell">Loading verifications...</div>
          ) : verifications.length === 0 ? (
            <div className="empty-cell">No verifications found</div>
          ) : (
            <table className="call-logs-table">
              <thead>
                <tr className="table-header-row">
                  <th className="table-header">NID Number</th>
                  <th className="table-header">Name (English)</th>
                  <th className="table-header">Name (Bengali)</th>
                  <th className="table-header">Date of Birth</th>
                  <th className="table-header">Mobile Number</th>
                  <th className="table-header">Blood Group</th>
                  <th className="table-header">Father's Name</th>
                  <th className="table-header">Mother's Name</th>
                  <th className="table-header">Birth Place</th>
                  <th className="table-header">Post Code</th>
                  <th className="table-header">Status</th>
                  <th className="table-header">Verified At</th>
                  <th className="table-header">Reference ID</th>
                </tr>
              </thead>
              <tbody>
                {verifications.map((item) => (
                  <tr
                    key={item.id}
                    className="table-row table-row-clickable"
                    onClick={() => {
                      setSelectedNid(item.nid_no);
                      setCurrentData(item);
                      setShowModal(true);
                    }}
                  >
                    <td className="table-cell">
                      <span className="phone-number-link">{item.nid_no || '-'}</span>
                    </td>
                    <td className="table-cell">{item.applicant_name_eng || '-'}</td>
                    <td className="table-cell">{item.applicant_name_ben || '-'}</td>
                    <td className="table-cell">{item.dob || '-'}</td>
                    <td className="table-cell">{item.mobile_no || '-'}</td>
                    <td className="table-cell">{item.blood_group || '-'}</td>
                    <td className="table-cell">{item.father_name || '-'}</td>
                    <td className="table-cell">{item.mother_name || '-'}</td>
                    <td className="table-cell">{item.birth_place || '-'}</td>
                    <td className="table-cell">{item.post_code || '-'}</td>
                    <td className="table-cell">
                      <span className={`status-badge ${getStatusClass(item.status, item.is_verified)}`}>
                        {getStatusText(item.status, item.is_verified)}
                      </span>
                    </td>
                    <td className="table-cell">{formatDate(item.created_at)}</td>
                    <td className="table-cell">
                      <span className="reference-id">{item.reference_id}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {pagination.total_items > 0 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {startItem}–{endItem} of {pagination.total_items}{' '}
              verifications (Page {pagination.current_page} of {pagination.total_pages})
            </div>
            <div className="pagination-buttons">
              <button
                onClick={() => handlePageChange(pagination.current_page - 1)}
                disabled={!pagination.has_prev}
                className={`pagination-btn ${!pagination.has_prev ? 'disabled' : ''}`}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(pagination.current_page + 1)}
                disabled={!pagination.has_next}
                className={`pagination-btn ${!pagination.has_next ? 'disabled' : ''}`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="custom-modal-overlay" onClick={resetStates}>
          <div className="nid-modal" onClick={(e) => e.stopPropagation()}>
            <div className="custom-modal-header">
              <h3>NID Verification Details</h3>
              <span className="custom-modal-close" onClick={resetStates}>×</span>
            </div>

            <div className="nid-modal-body">
              {currentData.message && (
                <div className={`nid-message ${currentData.status === 'failed' ? 'nid-message-error' : 'nid-message-info'}`}>
                  <strong>Message:</strong> {currentData.message}
                </div>
              )}

              <div className="nid-section">
                <h4 className="nid-section-title">NID Images</h4>
                <div className="nid-images-grid">
                  <div className="nid-image-container">
                    <p className="nid-image-label">Front Side</p>
                    {currentData.nid_front_image_url ? (
                      <img
                        src={currentData.nid_front_image_url}
                        alt="NID Front"
                        className="nid-image"
                        onClick={() => window.open(currentData.nid_front_image_url, '_blank')}
                      />
                    ) : (
                      <div className="nid-image-placeholder">No front image</div>
                    )}
                  </div>
                  <div className="nid-image-container">
                    <p className="nid-image-label">Back Side</p>
                    {currentData.nid_back_image_url ? (
                      <img
                        src={currentData.nid_back_image_url}
                        alt="NID Back"
                        className="nid-image"
                        onClick={() => window.open(currentData.nid_back_image_url, '_blank')}
                      />
                    ) : (
                      <div className="nid-image-placeholder">No back image</div>
                    )}
                  </div>
                </div>
              </div>

              <div className="nid-section">
                <h4 className="nid-section-title">Personal Information</h4>
                <div className="nid-info-grid">
                  <InfoItem label="NID Number" value={currentData.nid_no} />
                  <InfoItem label="Mobile Number" value={currentData.mobile_no} />
                  <InfoItem label="Name (English)" value={currentData.applicant_name_eng} />
                  <InfoItem label="Name (Bengali)" value={currentData.applicant_name_ben} />
                  <InfoItem label="Date of Birth" value={currentData.dob} />
                  <InfoItem label="Mobile Number" value={currentData.mobile_no} />
                  <InfoItem label="Blood Group" value={currentData.blood_group} />
                  <InfoItem label="Father's Name" value={currentData.father_name} />
                  <InfoItem label="Mother's Name" value={currentData.mother_name} />
                  <InfoItem label="Birth Place" value={currentData.birth_place} />
                  <InfoItem label="Post Code" value={currentData.post_code} />
                  <InfoItem label="Address" value={currentData.address} />
                </div>
              </div>

              <div className="nid-section">
                <h4 className="nid-section-title">System Information</h4>
                <div className="nid-info-grid">
                  <InfoItem label="Reference ID" value={currentData.reference_id} />
                  <InfoItem label="Status Code" value={currentData.status_code} />
                  <InfoItem
                    label="Current Status"
                    value={
                      <span className={`status-badge ${getStatusClass(currentData.status, currentData.is_verified)}`}>
                        {getStatusText(currentData.status, currentData.is_verified)}
                      </span>
                    }
                  />
                  <InfoItem label="Verified" value={currentData.is_verified ? 'Yes' : 'No'} />
                  <InfoItem label="Created At" value={formatDate(currentData.created_at)} />
                  <InfoItem label="Updated At" value={formatDate(currentData.updated_at)} />
                </div>
              </div>
            </div>

            <div className="custom-modal-footer">
              <button
                className="decline-btn"
                onClick={async () => {
                  const res = await updateNidStatus(currentData?.nid_no, 'failed', false);
                  if (res) { resetStates(); setChanged((p) => !p); }
                }}
              >
                Decline
              </button>
              <button
                className="approve-btn"
                onClick={async () => {
                  const res = await updateNidStatus(currentData?.nid_no, 'success', true);
                  if (res) { resetStates(); setChanged((p) => !p); }
                }}
              >
                Approve
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div className="nid-info-item">
    <p className="nid-info-label">{label}</p>
    <p className="nid-info-value">{value || '-'}</p>
  </div>
);

export default NidVerify;